import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  X,
  Calendar,
  Clock,
  User,
  Users,
  CheckCircle2,
  Circle,
  ExternalLink,
  FileText,
  Video,
  Book,
  Link as LinkIcon,
  GraduationCap,
  Paperclip,
  Star,
  MessageSquare
} from 'lucide-react'
import { Card, CardBody } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/contexts/Auth0Context'
import {
  Task,
  TaskSubmission,
  TaskFeedback
} from '@/types/task'

interface TaskDetailViewProps {
  task: Task
  submission?: TaskSubmission | null
  feedback?: TaskFeedback | null
  isOpen: boolean
  onClose: () => void
  onStatusChange?: (taskId: string, status: string) => void
  onSubmitWork?: () => void
  onProvideFeedback?: () => void
  onViewGroup?: (groupId: string) => void
}

const TaskDetailView: React.FC<TaskDetailViewProps> = ({
  task,
  submission,
  feedback,
  isOpen,
  onClose,
  onStatusChange,
  onSubmitWork,
  onProvideFeedback,
  onViewGroup
}) => {
  const { user } = useAuth()

  if (!isOpen) return null

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100'
      case 'in_progress':
        return 'text-accent-700 bg-accent-100'
      case 'under_review':
        return 'text-yellow-600 bg-yellow-100'
      case 'pending':
        return 'text-gray-600 bg-gray-100'
      case 'cancelled':
        return 'text-red-600 bg-red-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'critical':
        return 'text-red-600 bg-red-100'
      case 'high':
        return 'text-orange-600 bg-orange-100'
      case 'medium':
        return 'text-yellow-600 bg-yellow-100'
      case 'low':
        return 'text-green-600 bg-green-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const getResourceIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <Video className="w-4 h-4" />
      case 'document':
        return <FileText className="w-4 h-4" />
      case 'book':
        return <Book className="w-4 h-4" />
      case 'course':
        return <GraduationCap className="w-4 h-4" />
      case 'link':
      default:
        return <LinkIcon className="w-4 h-4" />
    }
  }

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'Not set'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'completed'

  const completedMilestones = task.milestones?.filter(m => m.isCompleted).length || 0
  const totalMilestones = task.milestones?.length || 0

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b px-6 py-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h2 className="text-xl font-semibold text-gray-900">{task.title}</h2>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                    {task.status.replace('_', ' ')}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                    {task.priority}
                  </span>
                  {task.groupId && (
                    <button
                      type="button"
                      onClick={() => onViewGroup?.(task.groupId!)}
                      className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors"
                    >
                      <Users className="w-3 h-3" />
                      Group assignment
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {task.mentor && (
                      <RouterLink to={`/users/${task.mentor.id}`} className="hover:underline">
                        {task.mentor.firstName} {task.mentor.lastName}
                      </RouterLink>
                    )}{' '}
                    (Mentor)
                  </span>
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {task.mentee && (
                      <RouterLink to={`/users/${task.mentee.id}`} className="hover:underline">
                        {task.mentee.firstName} {task.mentee.lastName}
                      </RouterLink>
                    )}{' '}
                    (Mentee)
                  </span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 ml-4"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Progress Section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Progress</span>
                <span className="text-sm text-gray-500">{task.progressPercentage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all ${
                    task.progressPercentage >= 100
                      ? 'bg-green-500'
                      : task.progressPercentage >= 50
                      ? 'bg-primary-500'
                      : 'bg-amber-500'
                  }`}
                  style={{ width: `${task.progressPercentage}%` }}
                />
              </div>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Calendar className={`w-5 h-5 ${isOverdue ? 'text-red-500' : 'text-gray-400'}`} />
                <div>
                  <p className="text-xs text-gray-500">Due Date</p>
                  <p className={`text-sm font-medium ${isOverdue ? 'text-red-600' : 'text-gray-900'}`}>
                    {formatDate(task.dueDate)}
                    {isOverdue && ' (Overdue)'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Time Tracking</p>
                  <p className="text-sm font-medium text-gray-900">
                    {task.actualHours || 0}h / {task.estimatedHours || '?'}h
                  </p>
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Description</h3>
              <p className="text-gray-600 whitespace-pre-wrap">{task.description}</p>
            </div>

            {/* Tags */}
            {task.tags && task.tags.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {task.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Milestones */}
            {task.milestones && task.milestones.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-700">
                    Milestones ({completedMilestones}/{totalMilestones})
                  </h3>
                </div>
                <div className="space-y-2">
                  {task.milestones.map((milestone, index) => (
                    <div
                      key={index}
                      className={`flex items-start gap-3 p-3 rounded-lg ${
                        milestone.isCompleted ? 'bg-green-50' : 'bg-gray-50'
                      }`}
                    >
                      {milestone.isCompleted ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5" />
                      ) : (
                        <Circle className="w-5 h-5 text-gray-400 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <p className={`font-medium ${milestone.isCompleted ? 'text-green-700' : 'text-gray-900'}`}>
                          {milestone.title}
                        </p>
                        {milestone.description && (
                          <p className="text-sm text-gray-500 mt-1">{milestone.description}</p>
                        )}
                        {milestone.dueDate && (
                          <p className="text-xs text-gray-400 mt-1">
                            Due: {formatDate(milestone.dueDate)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Resources */}
            {task.resources && task.resources.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Resources</h3>
                <div className="space-y-2">
                  {task.resources.map((resource, index) => (
                    <a
                      key={index}
                      href={resource.url || resource.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="text-gray-400">
                        {getResourceIcon(resource.type)}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{resource.title}</p>
                        {resource.description && (
                          <p className="text-sm text-gray-500">{resource.description}</p>
                        )}
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Submission */}
            {submission && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  <Paperclip className="w-4 h-4 inline mr-1" />
                  Submission
                </h3>
                <Card>
                  <CardBody>
                    <p className="text-gray-600 whitespace-pre-wrap mb-3">{submission.content}</p>
                    {submission.attachments && submission.attachments.length > 0 && (
                      <div className="border-t pt-3">
                        <p className="text-sm font-medium text-gray-700 mb-2">
                          Attachments ({submission.attachments.length})
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {submission.attachments.map((attachment, index) => (
                            <a
                              key={index}
                              href={attachment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm"
                            >
                              <FileText className="w-4 h-4 text-gray-500" />
                              <span className="text-gray-700">{attachment.filename}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    <p className="text-xs text-gray-400 mt-3">
                      Submitted: {formatDate(submission.submittedAt)}
                    </p>
                  </CardBody>
                </Card>
              </div>
            )}

            {/* Feedback */}
            {feedback && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  <MessageSquare className="w-4 h-4 inline mr-1" />
                  Mentor Feedback
                </h3>
                <Card>
                  <CardBody>
                    <div className="flex items-center gap-1 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-5 h-5 ${
                            star <= feedback.rating
                              ? 'text-yellow-400 fill-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                      <span className="ml-2 text-sm text-gray-600">
                        {feedback.rating}/5
                      </span>
                    </div>
                    <p className="text-gray-600 whitespace-pre-wrap">{feedback.comment}</p>
                    {feedback.suggestions && feedback.suggestions.length > 0 && (
                      <div className="mt-3 border-t pt-3">
                        <p className="text-sm font-medium text-gray-700 mb-2">Suggestions:</p>
                        <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                          {feedback.suggestions.map((suggestion, index) => (
                            <li key={index}>{suggestion}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <p className="text-xs text-gray-400 mt-3">
                      Feedback provided: {formatDate(feedback.createdAt)}
                    </p>
                  </CardBody>
                </Card>
              </div>
            )}
          </div>

          {/* Actions Footer */}
          <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex gap-3 justify-end">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>

            {/* Mentee Actions */}
            {user?.role === 'mentee' && task.status !== 'completed' && task.status !== 'cancelled' && (
              <>
                {task.status === 'pending' && onStatusChange && (
                  <Button
                    variant="primary"
                    onClick={() => onStatusChange(task.id, 'in_progress')}
                  >
                    Start Task
                  </Button>
                )}
                {task.status === 'in_progress' && onSubmitWork && (
                  <Button variant="primary" onClick={onSubmitWork}>
                    Submit Work
                  </Button>
                )}
              </>
            )}

            {/* Mentor Actions */}
            {user?.role === 'mentor' && task.status === 'under_review' && submission && !feedback && onProvideFeedback && (
              <Button variant="primary" onClick={onProvideFeedback}>
                <Star className="w-4 h-4 mr-2" />
                Provide Feedback
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default TaskDetailView
